import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Box,
    Typography,
    Alert,
    Switch,
    FormControlLabel,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Remove as RemoveIcon,
    CalendarMonth as CalendarMonthIcon,
    DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PartnerService from '../../Services/partner.service.js';
import partnerTaxAdvanceYearlyService from '../../Services/partnerTaxAdvanceYearly.service.js';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const PartnersManagementModal = ({ 
    open, 
    onClose, 
    caseId, 
    token, 
    direction = 'rtl' 
}) => {
    const { t } = useTranslation();
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [newPartner, setNewPartner] = useState({
        name: '',
        partnerId: '',
        equity: 0,
        withholdingTaxPercentage: 0,
        useIncomeTaxAdvancePercentage: true,
        incomeTaxAdvanceAmount: 1,
        year: new Date().getFullYear()
    });
    const [bulkPartners, setBulkPartners] = useState([]);
    const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
    const [isBulkMode, setIsBulkMode] = useState(false);

    // Per-partner yearly tax-advance settings (resolver-backed). Keyed by partner.id.
    // { [partnerId]: { rows: [{id, year, ...}], loaded: boolean } }
    const [yearlyByPartner, setYearlyByPartner] = useState({});
    // Which partner row has its yearly section expanded.
    const [openYearlyForPartnerId, setOpenYearlyForPartnerId] = useState(null);
    // Editing buffer for the year-row form per partner.
    // { [partnerId]: { year, usePercentage, percentage, amount, saving } }
    const [yearlyForm, setYearlyForm] = useState({});

    useEffect(() => {
        if (open && caseId && token) {
            fetchPartners();
        }
    }, [open, caseId, token]);

    useEffect(() => {
        if (!open) {
            setOpenYearlyForPartnerId(null);
            setYearlyByPartner({});
            setYearlyForm({});
        }
    }, [open]);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            const response = await PartnerService.getPartnersByCaseId(caseId,token);
            if (response.ok) {
                const data = await response.json();
                setPartners(data);
            } else {
                toast.error(t('FailedToFetchPartners'));
            }
        } catch (error) {
            console.error('Error fetching partners:', error);
            toast.error(t('FailedToFetchPartners'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Build a form-shape object for `partner` & `year`.
     * If a yearly row exists for that year => use it (auto-seed from saved values).
     * Otherwise => seed from legacy partner fields, mirroring the backend resolver.
     */
    const buildSeededYearlyForm = (partner, year, rows) => {
        const numericYear = parseInt(year, 10);
        const yearKey = Number.isNaN(numericYear) ? year : numericYear;
        const match = (Array.isArray(rows) ? rows : []).find(
            (r) => Number(r.year) === Number(yearKey)
        );
        if (match) {
            return {
                year: yearKey,
                usePercentage: !!match.useIncomeTaxAdvancePercentage,
                percentage: match.incomeTaxAdvancePercentage ?? '',
                amount: match.incomeTaxAdvanceAmount ?? '',
                saving: false
            };
        }
        return {
            year: yearKey,
            usePercentage: partner?.useIncomeTaxAdvancePercentage !== undefined
                ? !!partner.useIncomeTaxAdvancePercentage
                : true,
            percentage: partner?.withholdingTaxPercentage ?? '',
            amount: partner?.incomeTaxAdvanceAmount ?? '',
            saving: false
        };
    };

    /** Load yearly rows for a single partner if not already loaded. Returns the rows array. */
    const loadPartnerYearly = async (partner, { force = false } = {}) => {
        if (!partner || !caseId || !token) return [];
        if (!force && yearlyByPartner[partner.id]?.loaded) {
            return yearlyByPartner[partner.id].rows || [];
        }
        try {
            const data = await partnerTaxAdvanceYearlyService.getByPartner(token, caseId, partner.id);
            const rows = Array.isArray(data) ? data : [];
            setYearlyByPartner((prev) => ({
                ...prev,
                [partner.id]: { rows, loaded: true }
            }));
            return rows;
        } catch (error) {
            console.error('Error loading partner yearly rows:', error);
            toast.error(t('FailedToLoadYearlyTaxAdvance'));
            setYearlyByPartner((prev) => ({
                ...prev,
                [partner.id]: { rows: [], loaded: true }
            }));
            return [];
        }
    };

    const handleToggleYearly = async (partner) => {
        if (openYearlyForPartnerId === partner.id) {
            setOpenYearlyForPartnerId(null);
            return;
        }
        setOpenYearlyForPartnerId(partner.id);
        const initialYear = yearlyForm[partner.id]?.year ?? new Date().getFullYear();
        const rows = await loadPartnerYearly(partner);
        // Auto-seed the form: existing yearly row wins; otherwise legacy partner fields.
        setYearlyForm((prev) => ({
            ...prev,
            [partner.id]: buildSeededYearlyForm(partner, initialYear, rows)
        }));
    };

    /** Field updater. When `year` changes, re-seed % / ₪ / use-percentage from the matching row or legacy. */
    const updateYearlyFormField = (partnerId, field, value) => {
        if (field === 'year') {
            const partner = partners.find((p) => p.id === partnerId);
            const rows = yearlyByPartner[partnerId]?.rows || [];
            setYearlyForm((prev) => ({
                ...prev,
                [partnerId]: {
                    ...buildSeededYearlyForm(partner, value, rows),
                    saving: prev[partnerId]?.saving ?? false
                }
            }));
            return;
        }
        setYearlyForm((prev) => ({
            ...prev,
            [partnerId]: { ...(prev[partnerId] || {}), [field]: value }
        }));
    };

    const handleSavePartnerYearly = async (partner) => {
        const form = yearlyForm[partner.id];
        if (!form) return;
        const y = parseInt(form.year, 10);
        if (Number.isNaN(y) || y < 1900 || y > 2100) {
            toast.error(t('InvalidYear'));
            return;
        }
        updateYearlyFormField(partner.id, 'saving', true);
        try {
            await partnerTaxAdvanceYearlyService.upsert(token, {
                caseId,
                partnerId: partner.id,
                year: y,
                useIncomeTaxAdvancePercentage: !!form.usePercentage,
                incomeTaxAdvancePercentage: form.usePercentage
                    ? parseFloat(form.percentage || '0')
                    : null,
                incomeTaxAdvanceAmount: !form.usePercentage
                    ? parseFloat(form.amount || '0')
                    : null
            });
            toast.success(t('SavedSuccessfully'));
            const rows = await loadPartnerYearly(partner, { force: true });
            // Re-seed the form from the just-saved row so user sees the persisted values.
            setYearlyForm((prev) => ({
                ...prev,
                [partner.id]: buildSeededYearlyForm(partner, y, rows)
            }));
        } catch (error) {
            console.error('Error saving partner yearly row:', error);
            toast.error(t('SaveFailed'));
            updateYearlyFormField(partner.id, 'saving', false);
        }
    };

    const handleDeletePartnerYearly = async (partner, rowId) => {
        if (!rowId) return;
        try {
            await partnerTaxAdvanceYearlyService.remove(token, rowId, caseId);
            toast.success(t('DeletedSuccessfully'));
            const rows = await loadPartnerYearly(partner, { force: true });
            // Re-seed the form so it reflects the post-delete state for the current year.
            const currentYear = yearlyForm[partner.id]?.year ?? new Date().getFullYear();
            setYearlyForm((prev) => ({
                ...prev,
                [partner.id]: buildSeededYearlyForm(partner, currentYear, rows)
            }));
        } catch (error) {
            console.error('Error deleting partner yearly row:', error);
            toast.error(t('DeleteFailed'));
        }
    };

    const handleAddPartners = () => {
        if (!isBulkMode && !editingPartner) {
            // First time clicking - initialize bulk mode
            setIsBulkMode(true);
            setEditingPartner(null);
            setBulkPartners([{
                name: '',
                partnerId: '',
                equity: 0,
                withholdingTaxPercentage: 0,
                useIncomeTaxAdvancePercentage: true,
                incomeTaxAdvanceAmount: 0
            }]);
        } else if (isBulkMode) {
            // Already in bulk mode - add another row
            setBulkPartners([...bulkPartners, {
                name: '',
                partnerId: '',
                equity: 0,
                withholdingTaxPercentage: 0,
                useIncomeTaxAdvancePercentage: true,
                incomeTaxAdvanceAmount: 0
            }]);
        } else if (!editingPartner) {
            // Start single partner edit mode
            setEditingPartner('new');
            setNewPartner({
                name: '',
                partnerId: '',
                equity: 0,
                withholdingTaxPercentage: 0,
                useIncomeTaxAdvancePercentage: true,
                incomeTaxAdvanceAmount: 1,
                year: new Date().getFullYear()
            });
            setIsBulkMode(false);
            setBulkPartners([]);
        }
    };


    const handleRemoveBulkRow = (index) => {
        if (bulkPartners.length > 1) {
            setBulkPartners(bulkPartners.filter((_, i) => i !== index));
        }
    };

    const handleBulkPartnerChange = (index, field, value) => {
        const updatedPartners = [...bulkPartners];
        
        if (field === 'useIncomeTaxAdvancePercentage') {
            // When toggling the switch, reset the values
            updatedPartners[index] = {
                ...updatedPartners[index],
                useIncomeTaxAdvancePercentage: value,
                withholdingTaxPercentage: value ? updatedPartners[index].withholdingTaxPercentage : 0,
                incomeTaxAdvanceAmount: !value ? updatedPartners[index].incomeTaxAdvanceAmount : 0
            };
        } else {
            updatedPartners[index] = {
                ...updatedPartners[index],
                [field]: (field === 'equity' || field === 'withholdingTaxPercentage' || field === 'incomeTaxAdvanceAmount') ? (value === '' ? 0 : parseFloat(value) || 0) : value
            };
        }
        
        setBulkPartners(updatedPartners);
    };

    const handleEditPartner = (partner) => {
        setEditingPartner(partner.id);
        setNewPartner({
            name: partner.name,
            partnerId: partner.partnerId,
            equity: partner.equity,
            withholdingTaxPercentage: partner.withholdingTaxPercentage || 0,
            useIncomeTaxAdvancePercentage: partner.useIncomeTaxAdvancePercentage !== undefined ? partner.useIncomeTaxAdvancePercentage : true,
            incomeTaxAdvanceAmount: partner.incomeTaxAdvanceAmount || 1,
            year: new Date().getFullYear()
        });
        setIsBulkMode(false);
        setBulkPartners([]);
    };

    const handleDeletePartner = async (partnerId) => {
        try {
            const response = await PartnerService.deletePartner(token, partnerId, caseId);
            if (response.ok) {
                setPartners(partners.filter(p => p.id !== partnerId));
                toast.success(t('PartnerDeletedSuccessfully'));
            } else {
                toast.error(t('FailedToDeletePartner'));
            }
        } catch (error) {
            console.error('Error deleting partner:', error);
            toast.error(t('FailedToDeletePartner'));
        }
    };

    const handleSavePartner = async () => {
        if (!newPartner.name || !newPartner.partnerId || newPartner.equity <= 0) {
            toast.error(t('PleaseFillAllFields'));
            return;
        }

        const yearForCreate = parseInt(newPartner.year, 10);
        if (editingPartner === 'new' && (Number.isNaN(yearForCreate) || yearForCreate < 1900 || yearForCreate > 2100)) {
            toast.error(t('InvalidYear'));
            return;
        }

        try {
            setLoading(true);
            let response;

            if (editingPartner === 'new') {
                // Create new partner
                response = await PartnerService.createPartner(token, {
                    caseId: caseId,
                    name: newPartner.name,
                    partnerId: newPartner.partnerId,
                    equity: parseFloat(newPartner.equity),
                    withholdingTaxPercentage: parseFloat(newPartner.withholdingTaxPercentage) || 0,
                    useIncomeTaxAdvancePercentage: newPartner.useIncomeTaxAdvancePercentage,
                    incomeTaxAdvanceAmount: parseFloat(newPartner.incomeTaxAdvanceAmount) || 1
                });
            } else {
                // Update existing partner
                response = await PartnerService.updatePartner(token, {
                    id: editingPartner,
                    caseId: caseId,
                    name: newPartner.name,
                    partnerId: newPartner.partnerId,
                    equity: parseFloat(newPartner.equity),
                    withholdingTaxPercentage: parseFloat(newPartner.withholdingTaxPercentage) || 0,
                    useIncomeTaxAdvancePercentage: newPartner.useIncomeTaxAdvancePercentage,
                    incomeTaxAdvanceAmount: parseFloat(newPartner.incomeTaxAdvanceAmount) || 1
                });
            }

            if (response.ok) {
                // After CREATE: persist a yearly row for the chosen year so the resolver
                // returns those values for the requested year (instead of seeding from legacy).
                if (editingPartner === 'new') {
                    try {
                        const created = await response.clone().json();
                        const newPartnerId = created?.id || created?.Id;
                        if (newPartnerId) {
                            await partnerTaxAdvanceYearlyService.upsert(token, {
                                caseId,
                                partnerId: newPartnerId,
                                year: yearForCreate,
                                useIncomeTaxAdvancePercentage: !!newPartner.useIncomeTaxAdvancePercentage,
                                incomeTaxAdvancePercentage: newPartner.useIncomeTaxAdvancePercentage
                                    ? parseFloat(newPartner.withholdingTaxPercentage) || 0
                                    : null,
                                incomeTaxAdvanceAmount: !newPartner.useIncomeTaxAdvancePercentage
                                    ? parseFloat(newPartner.incomeTaxAdvanceAmount) || 0
                                    : null
                            });
                        }
                    } catch (yearlyErr) {
                        console.error('Error saving yearly row for new partner:', yearlyErr);
                        toast.warn(t('PartnerSavedButYearlyFailed') || t('SaveFailed'));
                    }
                }

                await fetchPartners(); // Refresh the list
                setEditingPartner(null);
                setNewPartner({ name: '', partnerId: '', equity: 0, withholdingTaxPercentage: 0, useIncomeTaxAdvancePercentage: true, incomeTaxAdvanceAmount: 1, year: new Date().getFullYear() });
                toast.success(t('PartnerSavedSuccessfully'));
            } else {
                toast.error(t('FailedToSavePartner'));
            }
        } catch (error) {
            console.error('Error saving partner:', error);
            toast.error(t('FailedToSavePartner'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingPartner(null);
        setNewPartner({ name: '', partnerId: '', equity: 0, withholdingTaxPercentage: 0, useIncomeTaxAdvancePercentage: true, incomeTaxAdvanceAmount: 1, year: new Date().getFullYear() });
        setIsBulkMode(false);
        setBulkPartners([]);
        setBulkYear(new Date().getFullYear());
    };

    const handleSaveBulkPartners = async () => {
        // Validate all bulk partners
        const invalidPartners = bulkPartners.filter(partner => 
            !partner.name || !partner.partnerId || partner.equity <= 0
        );

        if (invalidPartners.length > 0) {
            toast.error(t('PleaseFillAllFields'));
            return;
        }

        const yearForBulk = parseInt(bulkYear, 10);
        if (Number.isNaN(yearForBulk) || yearForBulk < 1900 || yearForBulk > 2100) {
            toast.error(t('InvalidYear'));
            return;
        }

        // Check if total equity equals 100%
        const totalEquity = calculateBulkTotalEquity();
        

        try {
            setLoading(true);
            const response = await PartnerService.bulkCreatePartners(token, {
                caseId: caseId,
                partners: bulkPartners.map(partner => ({
                    name: partner.name,
                    partnerId: partner.partnerId,
                    equity: parseFloat(partner.equity),
                    withholdingTaxPercentage: parseFloat(partner.withholdingTaxPercentage) || 0,
                    useIncomeTaxAdvancePercentage: partner.useIncomeTaxAdvancePercentage,
                    incomeTaxAdvanceAmount: parseFloat(partner.incomeTaxAdvanceAmount) || 1
                }))
            });

            if (response.ok) {
                // Persist a yearly row per created partner for the selected bulk year.
                try {
                    const createdPartners = await response.clone().json();
                    if (Array.isArray(createdPartners)) {
                        await Promise.all(createdPartners.map((cp, idx) => {
                            const formRow = bulkPartners[idx] || {};
                            const partnerIdGuid = cp?.id || cp?.Id;
                            if (!partnerIdGuid) return Promise.resolve();
                            return partnerTaxAdvanceYearlyService.upsert(token, {
                                caseId,
                                partnerId: partnerIdGuid,
                                year: yearForBulk,
                                useIncomeTaxAdvancePercentage: !!formRow.useIncomeTaxAdvancePercentage,
                                incomeTaxAdvancePercentage: formRow.useIncomeTaxAdvancePercentage
                                    ? parseFloat(formRow.withholdingTaxPercentage) || 0
                                    : null,
                                incomeTaxAdvanceAmount: !formRow.useIncomeTaxAdvancePercentage
                                    ? parseFloat(formRow.incomeTaxAdvanceAmount) || 0
                                    : null
                            });
                        }));
                    }
                } catch (yearlyErr) {
                    console.error('Error saving yearly rows for bulk partners:', yearlyErr);
                    toast.warn(t('PartnerSavedButYearlyFailed') || t('SaveFailed'));
                }

                await fetchPartners(); // Refresh the list
                setIsBulkMode(false);
                setBulkPartners([]);
                setBulkYear(new Date().getFullYear());
                toast.success(t('PartnersSavedSuccessfully'));
            } else {
                toast.error(t('FailedToSavePartners'));
            }
        } catch (error) {
            console.error('Error saving bulk partners:', error);
            toast.error(t('FailedToSavePartners'));
        } finally {
            setLoading(false);
        }
    };

    const calculateTotalEquity = () => {
        return partners.reduce((total, partner) => total + partner.equity, 0);
    };

    const calculateBulkTotalEquity = () => {
        const total = bulkPartners.reduce((total, partner) => {
            const equity = partner.equity || 0;
            return total + equity;
        }, 0);
        return total;
    };

    const handleSaveAndClose = async () => {
        const totalEquity = calculateTotalEquity();
        
        if (Math.abs(totalEquity - 100) > 0.01) { // Allow small floating point differences
            const result = await Swal.fire({
                title: t('TotalEquityNot100'),
                text: t('TotalEquityWarning', { total: totalEquity.toFixed(2) }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('Continue'),
                cancelButtonText: t('Cancel'),
                confirmButtonColor: '#304FFF',
                cancelButtonColor: '#d33'
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            dir={direction}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    maxHeight: '75vh',
                    height: 'auto',
                    width: '95%',
                    maxWidth: '700px'
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: '#f8f9fa',
                color: '#333',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid #e0e0e0'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        backgroundColor: '#e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <AddIcon sx={{ color: '#666', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {t('PartnersSettings')}
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 1, backgroundColor: '#fafafa', maxHeight: '50vh', overflow: 'auto' }}>
                <Box sx={{ 
                    mb: 1.5, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 ,mt:1}}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddPartners}
                            size="small"
                            disabled={editingPartner && !isBulkMode || (partners.length > 0 && Math.abs(calculateTotalEquity() - 100) <= 0.01)}
                            sx={{
                                backgroundColor: (editingPartner && !isBulkMode) || (partners.length > 0 && Math.abs(calculateTotalEquity() - 100) <= 0.01) ? '#ccc' : '#304FFF',
                                borderRadius: 1.5,
                                px: 2,
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: (editingPartner && !isBulkMode) || (partners.length > 0 && Math.abs(calculateTotalEquity() - 100) <= 0.01) ? 'none' : '0 2px 8px rgba(48, 79, 255, 0.3)',
                                '&:hover': {
                                    backgroundColor: (editingPartner && !isBulkMode) || (partners.length > 0 && Math.abs(calculateTotalEquity() - 100) <= 0.01) ? '#ccc' : '#1E40FF',
                                    boxShadow: (editingPartner && !isBulkMode) || (partners.length > 0 && Math.abs(calculateTotalEquity() - 100) <= 0.01) ? 'none' : '0 4px 12px rgba(48, 79, 255, 0.4)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            {isBulkMode ? t('AddAnotherPartner') : t('AddPartners')}
                        </Button>
                        {partners.length > 0 && Math.abs(calculateTotalEquity() - 100) <= 0.01 && (
                            <Typography variant="caption" sx={{ 
                                color: '#f44336', 
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textAlign: 'center'
                            }}>
                                {t('CannotAddMorePartners')}
                            </Typography>
                        )}
                    </Box>
                    
                    {partners.length > 0 && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            backgroundColor: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                        }}>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                {t('TotalEquity')}:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                                color: Math.abs(calculateTotalEquity() - 100) > 0.01 ? '#f44336' : '#4caf50',
                                fontWeight: 700
                            }}>
                                {calculateTotalEquity().toFixed(2)}%
                            </Typography>
                        </Box>
                    )}
                </Box>

                {editingPartner && !isBulkMode && (
                     <Paper sx={{ 
                         p: 1, 
                         mb: 1, 
                         borderRadius: 2,
                         boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                         border: '1px solid #e0e0e0',
                         backgroundColor: 'white'
                     }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 1,
                            pb: 0.5,
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '50%', 
                                backgroundColor: '#e9ecef',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <AddIcon sx={{ color: '#666', fontSize: 16 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ 
                                fontWeight: 600, 
                                color: '#333',
                                fontSize: '1rem'
                            }}>
                                {editingPartner === 'new' ? t('AddNewPartner') : t('EditPartner')}
                            </Typography>
                        </Box>
                        
                         <Box sx={{ 
                             display: 'flex', 
                             gap: 1, 
                             alignItems: 'flex-start', 
                             flexWrap: 'wrap',
                             mb: 1
                         }}>
                            <TextField
                                label={t('PartnerName')}
                                value={newPartner.name}
                                onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                                size="small"
                                sx={{ 
                                    minWidth: 200, 
                                    flex: '1 1 200px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: '#fafafa',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'white'
                                        }
                                    }
                                }}
                            />
                            <TextField
                                label={t('PartnerId')}
                                value={newPartner.partnerId}
                                onChange={(e) => setNewPartner({ ...newPartner, partnerId: e.target.value })}
                                size="small"
                                sx={{ 
                                    minWidth: 150, 
                                    flex: '1 1 150px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: '#fafafa',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'white'
                                        }
                                    }
                                }}
                            />
                            <TextField
                                label={t('Equity')}
                                type="number"
                                value={newPartner.equity}
                                onChange={(e) => setNewPartner({ ...newPartner, equity: parseFloat(e.target.value) || 0 })}
                                size="small"
                                sx={{ 
                                    minWidth: 120, 
                                    flex: '0 0 120px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: '#fafafa',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5'
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'white'
                                        }
                                    }
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                            />
                            <TextField
                                label={t('WithholdingTaxPercentage')}
                                type="number"
                                value={newPartner.withholdingTaxPercentage}
                                onChange={(e) => setNewPartner({ ...newPartner, withholdingTaxPercentage: parseFloat(e.target.value) || 0 })}
                                size="small"
                                disabled={!newPartner.useIncomeTaxAdvancePercentage}
                                sx={{ 
                                    minWidth: 150, 
                                    flex: '0 0 150px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: !newPartner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : '#fafafa',
                                        '&:hover': {
                                            backgroundColor: !newPartner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : '#f5f5f5'
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: !newPartner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : 'white'
                                        }
                                    }
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                            />
                        </Box>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            alignItems: 'flex-start', 
                            flexWrap: 'wrap',
                            mb: 1
                        }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={newPartner.useIncomeTaxAdvancePercentage}
                                        onChange={(e) => {
                                            const isPercentage = e.target.checked;
                                            setNewPartner({ 
                                                ...newPartner, 
                                                useIncomeTaxAdvancePercentage: isPercentage,
                                                withholdingTaxPercentage: isPercentage ? newPartner.withholdingTaxPercentage : 0,
                                                incomeTaxAdvanceAmount: !isPercentage ? newPartner.incomeTaxAdvanceAmount : 0
                                            });
                                        }}
                                        color="primary"
                                    />
                                }
                                label={t('UseIncomeTaxAdvancePercentage')}
                                sx={{ 
                                    minWidth: 200,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                            <TextField
                                label={t('IncomeTaxAdvanceAmount')}
                                type="number"
                                value={newPartner.incomeTaxAdvanceAmount}
                                onChange={(e) => setNewPartner({ ...newPartner, incomeTaxAdvanceAmount: parseFloat(e.target.value) || 1 })}
                                size="small"
                                disabled={newPartner.useIncomeTaxAdvancePercentage}
                                sx={{ 
                                    minWidth: 150, 
                                    flex: '0 0 150px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: newPartner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : '#fafafa',
                                        '&:hover': {
                                            backgroundColor: newPartner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : '#f5f5f5'
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: newPartner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : 'white'
                                        }
                                    }
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            {editingPartner === 'new' && (
                                <TextField
                                    label={t('Year')}
                                    type="number"
                                    value={newPartner.year}
                                    onChange={(e) => setNewPartner({ ...newPartner, year: e.target.value })}
                                    size="small"
                                    helperText={t('YearForInitialAdvanceRow') || ''}
                                    sx={{
                                        minWidth: 120,
                                        flex: '0 0 120px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5,
                                            backgroundColor: '#fafafa',
                                            '&:hover': { backgroundColor: '#f5f5f5' },
                                            '&.Mui-focused': { backgroundColor: 'white' }
                                        }
                                    }}
                                    inputProps={{ min: 1900, max: 2100, step: 1 }}
                                />
                            )}
                        </Box>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1.5, 
                            justifyContent: 'flex-end',
                            pt: 1,
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelEdit}
                                size="small"
                                sx={{
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: '#ddd',
                                    color: '#666',
                                    '&:hover': {
                                        borderColor: '#999',
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSavePartner}
                                disabled={loading}
                                size="small"
                                sx={{
                                    backgroundColor: '#304FFF',
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: '0 2px 8px rgba(48, 79, 255, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#1E40FF',
                                        boxShadow: '0 4px 12px rgba(48, 79, 255, 0.4)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#ccc',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Save')}
                            </Button>
                        </Box>
                    </Paper>
                )}

                {isBulkMode && (
                    <Paper sx={{ 
                        p: 1, 
                        mb: 1, 
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'white'
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 1,
                            pb: 0.5,
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '50%', 
                                backgroundColor: '#e9ecef',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <AddIcon sx={{ color: '#666', fontSize: 16 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ 
                                fontWeight: 600, 
                                color: '#333',
                                fontSize: '1rem'
                            }}>
                                {t('AddPartners')}
                            </Typography>
                            <TextField
                                label={t('Year')}
                                type="number"
                                value={bulkYear}
                                onChange={(e) => setBulkYear(e.target.value)}
                                size="small"
                                inputProps={{ min: 1900, max: 2100, step: 1 }}
                                sx={{
                                    width: 110,
                                    ml: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: '#fafafa'
                                    }
                                }}
                            />
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                backgroundColor: 'white',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1.5,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                ml: 'auto'
                            }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                    {t('TotalEquity')}:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    color: Math.abs(calculateBulkTotalEquity() - 100) > 0.01 ? '#f44336' : '#4caf50',
                                    fontWeight: 700
                                }}>
                                    {calculateBulkTotalEquity().toFixed(2)}%
                                </Typography>
                            </Box>
                        </Box>
                        
                        {bulkPartners.map((partner, index) => (
                            <Box key={index} sx={{ 
                                mb: 2,
                                p: 1,
                                backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                                borderRadius: 1,
                                border: '1px solid #f0f0f0'
                            }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 1, 
                                    alignItems: 'flex-start', 
                                    flexWrap: 'wrap',
                                    mb: 1
                                }}>
                                    <TextField
                                        label={`${t('PartnerName')} ${index + 1}`}
                                        value={partner.name}
                                        onChange={(e) => handleBulkPartnerChange(index, 'name', e.target.value)}
                                        size="small"
                                        sx={{ 
                                            minWidth: 200, 
                                            flex: '1 1 200px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5'
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white'
                                                }
                                            }
                                        }}
                                    />
                                    <TextField
                                        label={`${t('PartnerId')} ${index + 1}`}
                                        value={partner.partnerId}
                                        onChange={(e) => handleBulkPartnerChange(index, 'partnerId', e.target.value)}
                                        size="small"
                                        sx={{ 
                                            minWidth: 150, 
                                            flex: '1 1 150px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5'
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white'
                                                }
                                            }
                                        }}
                                    />
                                    <TextField
                                        label={`${t('Equity')} ${index + 1}`}
                                        type="number"
                                        value={partner.equity}
                                        onChange={(e) => handleBulkPartnerChange(index, 'equity', e.target.value)}
                                        size="small"
                                        sx={{ 
                                            minWidth: 120, 
                                            flex: '0 0 120px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5'
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white'
                                                }
                                            }
                                        }}
                                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    />
                                    <TextField
                                        label={`${t('IncomeTaxAdvancePercentage')} ${index + 1}`}
                                        type="number"
                                        value={partner.withholdingTaxPercentage}
                                        onChange={(e) => handleBulkPartnerChange(index, 'withholdingTaxPercentage', e.target.value)}
                                        size="small"
                                        disabled={!partner.useIncomeTaxAdvancePercentage}
                                        sx={{ 
                                            minWidth: 150, 
                                            flex: '0 0 150px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                backgroundColor: !partner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : 'white',
                                                '&:hover': {
                                                    backgroundColor: !partner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : '#f5f5f5'
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: !partner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : 'white'
                                                }
                                            }
                                        }}
                                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    />
                                </Box>
                                
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 1, 
                                    alignItems: 'flex-start', 
                                    flexWrap: 'wrap',
                                    mb: 1
                                }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={partner.useIncomeTaxAdvancePercentage}
                                                onChange={(e) => handleBulkPartnerChange(index, 'useIncomeTaxAdvancePercentage', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={`${t('UseIncomeTaxAdvancePercentage')} ${index + 1}`}
                                        sx={{ 
                                            minWidth: 200,
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                    <TextField
                                        label={`${t('IncomeTaxAdvanceAmount')} ${index + 1}`}
                                        type="number"
                                        value={partner.incomeTaxAdvanceAmount}
                                        onChange={(e) => handleBulkPartnerChange(index, 'incomeTaxAdvanceAmount', e.target.value)}
                                        size="small"
                                        disabled={partner.useIncomeTaxAdvancePercentage}
                                        sx={{ 
                                            minWidth: 150, 
                                            flex: '0 0 150px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                backgroundColor: partner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : 'white',
                                                '&:hover': {
                                                    backgroundColor: partner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : '#f5f5f5'
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: partner.useIncomeTaxAdvancePercentage ? '#f5f5f5' : 'white'
                                                }
                                            }
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                    {bulkPartners.length > 1 && (
                                        <IconButton
                                            onClick={() => handleRemoveBulkRow(index)}
                                            sx={{
                                                color: '#f44336',
                                                padding: 0.5,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                            }}
                                            size="small"
                                        >
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        ))}
                        
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1.5, 
                            justifyContent: 'flex-end',
                            pt: 1,
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelEdit}
                                size="small"
                                sx={{
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: '#ddd',
                                    color: '#666',
                                    '&:hover': {
                                        borderColor: '#999',
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveBulkPartners}
                                disabled={loading}
                                size="small"
                                sx={{
                                    backgroundColor: '#304FFF',
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: '0 2px 8px rgba(48, 79, 255, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#1E40FF',
                                        boxShadow: '0 4px 12px rgba(48, 79, 255, 0.4)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#ccc',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {t('Add')}
                            </Button>
                        </Box>
                    </Paper>
                )}

                <TableContainer component={Paper} sx={{ 
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden'
                }}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ 
                                    fontWeight: 600, 
                                    color: '#333',
                                    borderBottom: '1px solid #e0e0e0',
                                    py: 1.5,
                                    fontSize: '0.875rem'
                                }}>
                                    {t('PartnerName')}
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 600, 
                                    color: '#333',
                                    borderBottom: '1px solid #e0e0e0',
                                    py: 1.5,
                                    fontSize: '0.875rem'
                                }}>
                                    {t('PartnerId')}
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 600, 
                                    color: '#333',
                                    borderBottom: '1px solid #e0e0e0',
                                    py: 1.5,
                                    fontSize: '0.875rem'
                                }}>
                                    {t('Equity')} (%)
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 600, 
                                    color: '#333',
                                    borderBottom: '1px solid #e0e0e0',
                                    py: 1.5,
                                    fontSize: '0.875rem'
                                }}>
                                    {t('IncomeTaxAdvancePercentage')}/{t('WithholdingTaxPercentage')}
                                </TableCell>
                                <TableCell sx={{ 
                                    fontWeight: 600, 
                                    color: '#333',
                                    borderBottom: '1px solid #e0e0e0',
                                    py: 1.5,
                                    textAlign: 'center',
                                    fontSize: '0.875rem'
                                }}>
                                    {t('Actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {partners.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ 
                                        textAlign: 'center', 
                                        py: 3,
                                        color: '#666',
                                        fontStyle: 'italic',
                                        fontSize: '0.875rem'
                                    }}>
                                        {t('NoPartnersFound')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                partners.map((partner, index) => (
                                    <React.Fragment key={partner.id}>
                                    <TableRow 
                                        sx={{ 
                                            '&:hover': { 
                                                backgroundColor: '#f8f9fa' 
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#fafafa'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ 
                                            fontWeight: 500,
                                            py: 1.5,
                                            borderBottom: '1px solid #f0f0f0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {partner.name}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: '#666',
                                            py: 1.5,
                                            borderBottom: '1px solid #f0f0f0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {partner.partnerId}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontWeight: 600,
                                            color: '#495057',
                                            py: 1.5,
                                            borderBottom: '1px solid #f0f0f0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {partner.equity.toFixed(2)}%
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontWeight: 600,
                                            color: '#495057',
                                            py: 1.5,
                                            borderBottom: '1px solid #f0f0f0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {partner.useIncomeTaxAdvancePercentage 
                                                ? `${(partner.withholdingTaxPercentage || 0).toFixed(2)}%`
                                                : `${(partner.incomeTaxAdvanceAmount || 0).toFixed(2)} ₪`
                                            }
                                        </TableCell>
                                        <TableCell sx={{ 
                                            py: 1.5,
                                            borderBottom: '1px solid #f0f0f0',
                                            textAlign: 'center'
                                        }}>
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <IconButton
                                                    onClick={() => handleToggleYearly(partner)}
                                                    title={t('ManageYearlyTaxAdvance')}
                                                    sx={{
                                                        color: openYearlyForPartnerId === partner.id ? '#1E40FF' : '#304FFF',
                                                        backgroundColor: openYearlyForPartnerId === partner.id ? 'rgba(48, 79, 255, 0.12)' : 'transparent',
                                                        padding: 0.5,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(48, 79, 255, 0.1)'
                                                        },
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                    size="small"
                                                >
                                                    <CalendarMonthIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleEditPartner(partner)}
                                                    sx={{
                                                        color: '#304FFF',
                                                        padding: 0.5,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(48, 79, 255, 0.1)'
                                                        },
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                    size="small"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleDeletePartner(partner.id)}
                                                    sx={{
                                                        color: '#f44336',
                                                        padding: 0.5,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                                        },
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                    size="small"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {openYearlyForPartnerId === partner.id && (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ p: 0, borderBottom: '1px solid #e0e0e0' }}>
                                                <Box sx={{
                                                    backgroundColor: '#f4f6ff',
                                                    p: 1.5,
                                                    borderTop: '2px solid #304FFF'
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <CalendarMonthIcon sx={{ color: '#304FFF', fontSize: 20 }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#304FFF' }}>
                                                            {t('ManageYearlyTaxAdvance')}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#666', ml: 1 }}>
                                                            {partner.name}
                                                        </Typography>
                                                    </Box>

                                                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                                                        {t('ManageYearlyTaxAdvanceHelp')}
                                                    </Typography>

                                                    {/* Existing rows */}
                                                    <TableContainer component={Paper} sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                                        <Table size="small">
                                                            <TableHead sx={{ backgroundColor: '#fafafa' }}>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                                        {t('Year')}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                                        {t('IncomeTaxAdvancePercentage')}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                                        {t('IncomeTaxAdvanceAmount')}
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                                        {t('UseIncomeTaxAdvancePercentage')}
                                                                    </TableCell>
                                                                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                                        {t('Actions')}
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {(yearlyByPartner[partner.id]?.rows || []).map((r) => (
                                                                    <TableRow key={r.id}>
                                                                        <TableCell sx={{ fontSize: '0.8rem' }}>{r.year}</TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                                                            {r.incomeTaxAdvancePercentage ?? '—'}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                                                            {r.incomeTaxAdvanceAmount ?? '—'}
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: '0.8rem' }}>
                                                                            {r.useIncomeTaxAdvancePercentage ? t('Yes') : t('No')}
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeletePartnerYearly(partner, r.id)}
                                                                                aria-label="delete"
                                                                            >
                                                                                <DeleteOutlineIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                                {(!yearlyByPartner[partner.id] || yearlyByPartner[partner.id].rows.length === 0) && (
                                                                    <TableRow>
                                                                        <TableCell colSpan={5} sx={{ textAlign: 'center', color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>
                                                                            {t('NoYearlyRowsYet')}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>

                                                    {/* Add or update form */}
                                                    {(() => {
                                                        const form = yearlyForm[partner.id] || { year: new Date().getFullYear(), usePercentage: true, percentage: '', amount: '', saving: false };
                                                        return (
                                                            <Box sx={{
                                                                p: 1,
                                                                backgroundColor: 'white',
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: 1
                                                            }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#333', display: 'block', mb: 1 }}>
                                                                    {t('AddOrUpdateYear')}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                    {(() => {
                                                                        const savedYears = (yearlyByPartner[partner.id]?.rows || [])
                                                                            .map((r) => Number(r.year))
                                                                            .filter((y) => !Number.isNaN(y))
                                                                            .sort((a, b) => b - a);
                                                                        return (
                                                                            <Autocomplete
                                                                                freeSolo
                                                                                size="small"
                                                                                options={savedYears.map((y) => String(y))}
                                                                                value={form.year != null ? String(form.year) : ''}
                                                                                onChange={(_e, newValue) => {
                                                                                    updateYearlyFormField(partner.id, 'year', newValue ?? '');
                                                                                }}
                                                                                onInputChange={(_e, newInput) => {
                                                                                    updateYearlyFormField(partner.id, 'year', newInput ?? '');
                                                                                }}
                                                                                sx={{ width: 140 }}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        label={t('Year')}
                                                                                        type="number"
                                                                                        inputProps={{ ...params.inputProps, inputMode: 'numeric' }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        );
                                                                    })()}
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={!!form.usePercentage}
                                                                                onChange={(e) => updateYearlyFormField(partner.id, 'usePercentage', e.target.checked)}
                                                                                color="primary"
                                                                            />
                                                                        }
                                                                        label={t('UseIncomeTaxAdvancePercentage')}
                                                                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                                                                    />
                                                                    {form.usePercentage ? (
                                                                        <TextField
                                                                            label={t('IncomeTaxAdvancePercentage')}
                                                                            type="number"
                                                                            value={form.percentage}
                                                                            onChange={(e) => updateYearlyFormField(partner.id, 'percentage', e.target.value)}
                                                                            size="small"
                                                                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                                                                            sx={{ width: 180 }}
                                                                        />
                                                                    ) : (
                                                                        <TextField
                                                                            label={t('IncomeTaxAdvanceAmount')}
                                                                            type="number"
                                                                            value={form.amount}
                                                                            onChange={(e) => updateYearlyFormField(partner.id, 'amount', e.target.value)}
                                                                            size="small"
                                                                            inputProps={{ min: 0, step: 0.01 }}
                                                                            sx={{ width: 180 }}
                                                                        />
                                                                    )}
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        startIcon={<SaveIcon />}
                                                                        onClick={() => handleSavePartnerYearly(partner)}
                                                                        disabled={!!form.saving}
                                                                        sx={{
                                                                            backgroundColor: '#304FFF',
                                                                            textTransform: 'none',
                                                                            '&:hover': { backgroundColor: '#1E40FF' }
                                                                        }}
                                                                    >
                                                                        {t('Save')}
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })()}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ 
                p: 1, 
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e0e0e0',
                gap: 1.5
            }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined"
                    size="small"
                    sx={{
                        borderRadius: 1.5,
                        px: 2,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#ddd',
                        color: '#666',
                        '&:hover': {
                            borderColor: '#999',
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                >
                    {t('Close')}
                </Button>
                <Button 
                    onClick={handleSaveAndClose} 
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: '#304FFF',
                        borderRadius: 1.5,
                        px: 2,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(48, 79, 255, 0.3)',
                        '&:hover': {
                            backgroundColor: '#1E40FF',
                            boxShadow: '0 4px 12px rgba(48, 79, 255, 0.4)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    {t('SaveAndClose')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PartnersManagementModal;
